import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
	Box,
	Typography,
	Paper,
	Button,
	CircularProgress,
	Chip,
	TextField,
} from "@mui/material";
import supabase from "../../supabaseClient";
import { getImageUrl } from "../../utils/imageUrl";

const StoreItemDetail = () => {
	const { storeItemId } = useParams();
	const [item, setItem] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [quantity, setQuantity] = useState(1);
	const navigate = useNavigate();

	useEffect(() => {
		async function fetchItem() {
			setLoading(true);
			setError("");
			try {
				// Use Supabase client for auth-aware query
				const { data, error } = await supabase
					.from("store_items")
					.select(`*, seasons(*), store_item_plants(*, plants(*))`)
					.eq("id", storeItemId)
					.single();
				if (data) {
					setItem(data);
				} else {
					setError(error?.message || "Store item not found.");
				}
			} catch (err) {
				setError("Failed to load store item.");
			}
			setLoading(false);
		}
		fetchItem();
	}, [storeItemId]);

	const handleAddToCart = () => {
		// Get current cart from localStorage or initialize empty array
		const cart = JSON.parse(localStorage.getItem("cart")) || [];
		// Check if item already exists in cart
		const existingIndex = cart.findIndex((ci) => ci.id === item.id);
		if (existingIndex !== -1) {
			// If exists, update quantity
			cart[existingIndex].quantity += quantity;
		} else {
			// Add new item
			cart.push({
				id: item.id,
				title: item.title,
				price_cents: item.price_cents,
				photo_url: item.photo_url,
				type: item.type,
				season: item.seasons?.name || null,
				quantity,
				// add any other fields you want to persist
			});
		}
		localStorage.setItem("cart", JSON.stringify(cart));
		navigate("/cart");
	};

	const formatDateOnly = (dateStr) => {
		if (!dateStr) return "";
		const [y, m, d] = dateStr.split("-");
		return `${new Date(y, m - 1, d).toLocaleDateString("en-US", {
			year: "numeric",
			month: "long",
			day: "numeric",
		})}`;
	};

	if (loading)
		return (
			<Box sx={{ textAlign: "center", mt: 4 }}>
				<CircularProgress />
			</Box>
		);
	if (error)
		return (
			<Box sx={{ textAlign: "center", mt: 4 }}>
				<Typography color="error">{error}</Typography>
			</Box>
		);
	if (!item) return null;

	return (
		<Box sx={{ maxWidth: 700, mx: "auto", mt: 4 }}>
			<Paper sx={{ p: 3 }}>
				<Box display="flex" justifyContent="space-between" alignItems="center">
					<Typography variant="h5">{item.title}</Typography>
					<Chip
						label={
							item.type === "preorder"
								? "Preorder"
								: item.type === "in_stock"
								? "In Stock"
								: "Bundle"
						}
						color={item.type === "preorder" ? "primary" : "default"}
					/>
				</Box>
				<Box display="flex" alignItems="flex-start" pt={2}>
					<Box sx={{ flex: 1, pr: 2 }}>
						<Typography variant="body1" mb={1}>
							{item.description}
						</Typography>
						<Typography variant="subtitle1" mb={1}>
							${(item.price_cents / 100).toFixed(2)}
						</Typography>
						{item.seasons && item.seasons.name && (
							<Typography variant="body2" color="text.secondary">
								Season: {item.seasons.name}
							</Typography>
						)}
						{item.type === "preorder" &&
							item.seasons &&
							item.seasons.fulfillment_start &&
							item.seasons.fulfillment_end && (
								<Typography variant="body2" color="info.main">
									{formatDateOnly(item.seasons.fulfillment_start) ===
									formatDateOnly(item.seasons.fulfillment_end)
										? `Estimated pickup: ${formatDateOnly(
												item.seasons.fulfillment_start
										  )}`
										: `Estimated pickup: ${formatDateOnly(
												item.seasons.fulfillment_start
										  )} - ${formatDateOnly(item.seasons.fulfillment_end)}`}
								</Typography>
							)}
					</Box>
					{item.photo_url && (
						<Box
							sx={{
								minWidth: 120,
								maxWidth: 200,
								ml: 2,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<a
								href={getImageUrl(item.photo_url)}
								target="_blank"
								rel="noopener noreferrer"
							>
								<img
									src={getImageUrl(item.photo_url)}
									alt={item.title}
									loading="lazy"
									style={{
										maxWidth: "100%",
										maxHeight: 180,
										borderRadius: 8,
										cursor: "pointer",
									}}
								/>
							</a>
						</Box>
					)}
				</Box>
				<Box>
					{item.store_item_plants && item.store_item_plants.length > 0 && (
						<Box mt={1}>
							<Typography variant="body2" fontWeight="bold">
								Plants Included:
							</Typography>
							<ul>
								{item.store_item_plants.map((sip) => (
									<li key={sip.plant_id}>
										{sip.plants?.plant_name || "Unnamed Plant"}{" "}
										{sip.quantity_estimate ? `(${sip.quantity_estimate})` : ""}
									</li>
								))}
							</ul>
						</Box>
					)}
					<Box mt={2} display="flex" alignItems="center">
						<TextField
							label="Quantity"
							type="number"
							value={quantity}
							onChange={(e) =>
								setQuantity(Math.max(1, parseInt(e.target.value) || 1))
							}
							sx={{ width: 120, mr: 2 }}
							inputProps={{ min: 1 }}
						/>
						<Button variant="contained" onClick={handleAddToCart}>
							Add to Cart
						</Button>
					</Box>
				</Box>
			</Paper>
		</Box>
	);
};

export default StoreItemDetail;
